import { Link } from 'react-router-dom'
import type { Camera, HealthStatus, CameraHealth } from '../../types'

const HEALTH_COLORS: Record<HealthStatus, string> = {
  online: 'bg-success',
  offline: 'bg-error',
  degraded: 'bg-warning',
  unknown: 'bg-gray-300',
}

const HEALTH_LABELS: Record<HealthStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  degraded: 'Degraded',
  unknown: 'Not checked',
}

function healthTooltip(h: CameraHealth | null): string {
  if (!h) return 'Not checked'
  const parts = [HEALTH_LABELS[h.status]]
  if (h.error_message) parts.push(`Error: ${h.error_message}`)
  if (h.device_model) parts.push(`Model: ${h.device_model}`)
  if (h.firmware_version) parts.push(`FW: ${h.firmware_version}`)
  if (h.last_seen_at) parts.push(`Last seen: ${new Date(h.last_seen_at).toLocaleString()}`)
  if (h.checked_at) parts.push(`Checked: ${new Date(h.checked_at).toLocaleString()}`)
  return parts.join('\n')
}

interface Props {
  camera: Camera
  siteId: string
  onEdit: (camera: Camera) => void
  onDelete: (camera: Camera) => void
  onSettings: (camera: Camera) => void
  canManage: boolean
  healthStatus?: HealthStatus
  healthDetail?: CameraHealth | null
}

export function CameraCard({ camera, siteId, onEdit, onDelete, onSettings, canManage, healthStatus = 'unknown', healthDetail }: Props) {
  const actionCls = 'text-xs px-2.5 py-1.5 rounded transition-colors'

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${HEALTH_COLORS[healthStatus]} shrink-0`} title={healthTooltip(healthDetail ?? null)} />
            <h4 className="text-sm font-semibold text-gray-900 truncate">{camera.name}</h4>
            <span className="text-[10px] text-gray-400 font-mono shrink-0">#{camera.cam_number}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-4">
            {camera.cam_ip}:{camera.port}
            {camera.stream_name && <span className="ml-1.5 font-mono text-gray-400">{camera.stream_name}</span>}
          </p>
          {camera.external_ip && (
            <p className="text-[10px] text-gray-400 mt-0.5 ml-4">
              External: {camera.external_ip}
            </p>
          )}
          {healthDetail && (healthStatus === 'offline' || healthStatus === 'degraded') && (
            <p className="text-[10px] text-error/80 mt-0.5 ml-4">
              {healthDetail.error_message ?? 'Unreachable'}
              {healthDetail.last_seen_at && (
                <span className="text-gray-400 ml-1.5">
                  Last seen: {new Date(healthDetail.last_seen_at).toLocaleString()}
                </span>
              )}
            </p>
          )}
          {healthDetail?.device_model && healthStatus === 'online' && (
            <p className="text-[10px] text-gray-400 mt-0.5 ml-4">
              {healthDetail.device_model}
              {healthDetail.firmware_version && ` · FW ${healthDetail.firmware_version}`}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-3 ml-4">
        <Link
          to={`/sites/${siteId}/viewer`}
          className={`${actionCls} bg-brand/10 text-brand hover:bg-brand/20 font-medium`}
        >
          Live View
        </Link>
        <button
          onClick={() => onSettings(camera)}
          className={`${actionCls} bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900`}
        >
          Settings
        </button>
        {canManage && (
          <>
            <button
              onClick={() => onEdit(camera)}
              className={`${actionCls} text-gray-400 hover:text-gray-700`}
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(camera)}
              className={`${actionCls} text-gray-400 hover:text-error`}
            >
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  )
}
